import {
	DetailsRow,
	IDetailsRowBaseProps,
	IDetailsRowProps,
	Stack,
	Toggle,
	useTheme,
	Text,
	IconButton,
	IColumn,
} from '@fluentui/react';
import React, {useEffect, useMemo, useState} from 'react';
import {getListClassNames} from 'styles';
import {WorkbenchHeader} from '..';
import {useDashboardQueries} from 'features/Dashboard/hooks/useDashboardQueries';
import {LoadWrapper} from 'components/LoadWrapper';
import {DashboardTooltipTranslationProvider} from '../DashboardTooltipTranslationProvider';
import {EntityList} from 'components';
import {TFunction} from 'i18next';
import {useDashboardRenderers} from 'features/Dashboard/hooks/useDashboardRenderers';
import {useVkoApproverWorkbenchStyles} from './useVkoApproverWorkbenchStyles';
import {useHiddenRegDocState} from 'features/Dashboard/hooks/useHiddenRegDocState';
import {ActivityListHelper} from 'components/ActivityList/_ActivityListHelper';
import {useVkoApproverWorkbenchUtils} from '././useVkoApproverWorkbenchUtils';

export type VkoApproverTileProps = {
	halfHeight?: boolean;
	title: string;
	t: TFunction;
	itemCountLoaded?: (count: number) => void;
};
export const VkoApproverMyTodoTile: React.FC<VkoApproverTileProps> = ({
	halfHeight,
	title,
	t,
	itemCountLoaded,
}) => {
	const theme = useTheme();
	const classNames = getListClassNames(theme, halfHeight);
	const {renderWorkflowStatus} = useDashboardRenderers();
	const {
		widthSTD40,
		widthSTD120,
		widthSTD180,
		classNames: vkoApproverClassNames,
	} = useVkoApproverWorkbenchStyles();
	const {renderRegulatoryDocumentColumnField, sortAndHideByCreationDate} =
		useVkoApproverWorkbenchUtils();
	const {getVkoApproverTodoItems} = useDashboardQueries();
	const {setIsHidden, isHidden} = useHiddenRegDocState();
	const {data, loading} = getVkoApproverTodoItems();
	const [entityListKey, setEntityListKey] = useState(0);
	const [hiddenExpanded, setHiddenExpanded] = useState(true);

	useEffect(() => {
		if (!loading && data && itemCountLoaded) {
			itemCountLoaded(data.length);
		}
	}, [loading, data, itemCountLoaded]);

	const sortedData = useMemo(() => {
		return sortAndHideByCreationDate(data, false, isHidden, hiddenExpanded);
	}, [data, hiddenExpanded, entityListKey]);

	const columns = useMemo(
		() => [
			{
				key: 'name',
				name: t('RegulatoryDocument'),
				fieldName: 'name',
				...widthSTD180,
				onRender: renderRegulatoryDocumentColumnField,
				sortable: true,
				customSorting(
					items: any[],
					column: IColumn,
					isSortedDescending?: boolean,
				) {
					return sortAndHideByCreationDate(
						items.slice(0),
						!isSortedDescending,
						isHidden,
						hiddenExpanded,
					);
				},
			},
			{
				key: 'hidden',
				name: t('Hidden'),
				fieldName: 'hidden',
				...widthSTD40,
				onRender(item: any) {
					return (
						<Toggle
							checked={isHidden(item.id)}
							onChange={(e, val) => {
								const newVal = Boolean(val);
								setIsHidden(item.id, newVal);
								setEntityListKey(entityListKey + 1);
								e.preventDefault();
							}}
						/>
					);
				},
			},
			{
				key: 'workflow.status',
				name: t('WorkflowStatus'),
				fieldName: 'workflow.status',
				...widthSTD120,
				onRender: renderWorkflowStatus,
			},
		],
		[
			entityListKey,
			isHidden,
			renderRegulatoryDocumentColumnField,
			renderWorkflowStatus,
			setIsHidden,
			sortAndHideByCreationDate,
		],
	);

	const itemList = useMemo(
		() => (
			<DashboardTooltipTranslationProvider>
				<EntityList
					key={entityListKey}
					styles={{root: {maxWidth: '240px'}}}
					sticky={false}
					selectionMode={0}
					items={sortedData}
					columns={columns}
					onShouldVirtualize={() => true}
					onRenderRow={(props?: IDetailsRowProps) => {
						if (props?.item?.type === 'Separator') {
							return (
								<Stack
									horizontal
									styles={{
										root: {
											backgroundColor: theme.palette.neutralLight,
											width: 520,
										},
									}}
								>
									<Stack.Item>
										<Text className={classNames.workbench}>{`${t(
											'HiddenSeparator',
										)}`}</Text>
									</Stack.Item>
									<Stack.Item>
										<IconButton
											iconProps={{
												iconName: hiddenExpanded
													? 'DoubleChevronUp'
													: 'DoubleChevronDown',
											}}
											styles={{
												root: {
													width: 18,
													height: 18,
													color: 'black',
												},
												icon: {fontSize: 9},
											}}
											onClick={() => setHiddenExpanded(!hiddenExpanded)}
										></IconButton>
									</Stack.Item>
								</Stack>
							);
						}
						return (
							<a
								href={ActivityListHelper.getRegulatoryDocumentLink(props?.item)}
							>
								<DetailsRow {...(props as IDetailsRowBaseProps)} />
							</a>
						);
					}}
				/>
			</DashboardTooltipTranslationProvider>
		),
		[columns, entityListKey, hiddenExpanded, sortedData],
	);

	return (
		<div className={vkoApproverClassNames.roundedDashboardActivityContainer}>
			<WorkbenchHeader
				halfHeight
				title={title}
				items={data.length}
				hideNewWindowIcon={true}
			/>
			{loading ? (
				<LoadWrapper loading={true} />
			) : (
				<div className={classNames.dashboardActivityContent} data-is-scrollable>
					{itemList}
				</div>
			)}
		</div>
	);
};
